import { Plan } from "./user";

/**
 * Date Model
 */

export interface BillingInfo {
  plan: Plan;
  stripeId?: string;
  platform?: string;
  cardInfo?: CardInfo;
  subscription?: Subscription;
  invoiceUrl?: string;
}

/** App課金の platform 値一覧。他画面でも課金経路判定に使うためここで一元管理する */
export const APP_PLATFORMS = ["ios", "android"] as const;

export function isAppPlatform(platform?: string): boolean {
  return !!platform && (APP_PLATFORMS as readonly string[]).includes(platform);
}

interface CardInfo {
  // eslint-disable-next-line camelcase
  exp_month: string;
  // eslint-disable-next-line camelcase
  exp_year: string;
  last4: string;
}

/**
 * App の契約状態モデル（RevenueCat v2 由来）を正とする契約更新状態。
 * ACTIVE: 継続中 / WILL_CHANGE: プラン変更予定（契約は継続） / WILL_CANCEL: 解約予定 / PAYMENT_ERROR: 決済エラー・リトライ中
 */
export type SubscriptionRenewalState = "ACTIVE" | "WILL_CHANGE" | "WILL_CANCEL" | "PAYMENT_ERROR";

interface Subscription {
  created: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  isAutoRenewal: boolean;
  // NOTE: backend未反映の間は undefined になりうる。移行期は isAutoRenewal 等へのフォールバックが必要
  renewalState?: SubscriptionRenewalState;
}

/**
 * 解約済み（解約バッジ・終了日表示）かどうかを判定する。
 * WILL_CHANGE（プラン変更予定）は契約継続中のため ACTIVE と同じ扱い（false）にする。
 * renewalState が未定義（backend未反映）の間は isAutoRenewal ベースの旧判定にフォールバックする。
 */
export function resolveIsCanceled(
  renewalState: SubscriptionRenewalState | null | undefined,
  fallback: { isFree: boolean; isAutoRenewal?: boolean }
): boolean {
  if (renewalState) {
    return renewalState === "WILL_CANCEL";
  }
  // TODO: backendのrenewalState反映後に削除
  return !fallback.isFree && fallback.isAutoRenewal === false;
}

/**
 * 決済エラー・リトライ中かどうかを判定する。
 * renewalState が未定義（backend未反映）の間は invoiceUrl ベースの旧判定（legacyHasPaymentError）にフォールバックする。
 */
export function resolveHasPaymentError(
  renewalState: SubscriptionRenewalState | null | undefined,
  legacyHasPaymentError: boolean
): boolean {
  if (renewalState) {
    return renewalState === "PAYMENT_ERROR";
  }
  // TODO: backendのrenewalState反映後に削除
  return legacyHasPaymentError;
}

/**
 * Request Model
 */

export interface RegisterCustomerInput {
  token: string;
  verificationToken: string;
  email: string;
}

export interface SubscriptionInput {
  plan: Plan;
  promotionCode?: string;
}

export interface CreateCheckoutSessionInput {
  /** メールアドレス */
  email: string;
  /** サブスクリプションプラン */
  plan: Plan;
  /** 成功時のリダイレクト先URL */
  successUrl: string;
  /** キャンセル時のリダイレクト先URL */
  cancelUrl: string;
}

export interface CreatePortalSessionInput {
  /** リターン時のリダイレクト先URL */
  returnUrl: string;
}

export interface CreateOneTimeCheckoutSessionInput {
  /** メールアドレス */
  email: string;
  /** 商品ID */
  itemId: string;
  /** 成功時のリダイレクト先URL */
  successUrl: string;
  /** キャンセル時のリダイレクト先URL */
  cancelUrl: string;
}

/**
 * Response Model
 */

export interface CheckoutSession {
  id: string;
  url: string;
}

export interface PortalSession {
  id: string;
  url: string;
}

export interface PlanPricing {
  monthlyPrice: number;
  yearlyPrice: number;
}
